import { Injectable } from '@angular/core';
import { AudioWaveform } from './audio.model';
import { computePeaksFromSamples } from './audio-waveform.utils';

const DECODE_SAMPLE_RATE = 16000;

@Injectable({ providedIn: 'root' })
export class AudioWaveformService {
  private readonly waveforms = new Map<string, Promise<AudioWaveform | null>>();

  loadWaveform(url: string): Promise<AudioWaveform | null> {
    let waveform = this.waveforms.get(url);
    if (!waveform) {
      waveform = this.decodeWaveform(url);
      this.waveforms.set(url, waveform);
    }
    return waveform;
  }

  // Decoding is the only reliable duration source: MediaRecorder WebM files
  // carry no duration header, so `<audio>.duration` stays `Infinity` there.
  // Plain `fetch` rather than HttpClient so the API auth header is not sent to
  // the storage host.
  private async decodeWaveform(url: string): Promise<AudioWaveform | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const encodedAudio = await response.arrayBuffer();
      const context = new OfflineAudioContext(1, 1, DECODE_SAMPLE_RATE);
      const audioBuffer = await context.decodeAudioData(encodedAudio);
      return {
        peaks: computePeaksFromSamples(audioBuffer.getChannelData(0)),
        durationSeconds: audioBuffer.duration,
      };
    } catch {
      this.waveforms.delete(url);
      return null;
    }
  }
}
